import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, mediaUrl, apiError, bgUrl, BACKGROUNDS, FEELINGS } from "../lib/api";
import { processImage } from "../lib/image";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { useSiteContent } from "../context/SiteContentContext";
import { Avatar } from "../components/Avatar";
import { PostCard } from "../components/PostCard";
import { ImageCropper } from "../components/ImageCropper";
import { StatusComposer } from "../components/StatusComposer";
import { WallComposer } from "../components/WallComposer";
import { PhotoViewer } from "../components/PhotoViewer";
import { VideoViewer } from "../components/VideoViewer";
import { ReportDialog } from "../components/ReportDialog";
import { ProfilePlaylist } from "../components/ProfilePlaylist";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Camera, UserPlus, UserCheck, MessageCircle, MoreHorizontal, Ban, Flag, Loader2, ShieldCheck, Check, X, Clock, Star, Pencil, Heart, Music, MapPin, Cake, Sparkles, Crown, Eye, Users, ArrowUpDown, EyeOff, ImagePlus, Trash2, Lock, Globe, Plus, Video, Play, MessageSquare, Tag, GripVertical, ChevronUp, ChevronDown, Pin } from "lucide-react";
import { InviteButton } from "../components/InviteFriends";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const SEXUALITY = ["Asexual", "Bisexual", "Demisexual", "Heterosexual", "Homosexual", "Pansexual"];
const SOCIAL_PLATFORMS = ["Instagram", "Facebook", "X", "TikTok", "Snapchat", "YouTube", "Twitch", "Discord", "Website", "Other"];
const feelingEmoji = (label) => FEELINGS.find((f) => f.label === label)?.emoji || "✨";

function toMusicEmbed(url) {
  if (!url) return null;
  let raw = String(url).trim();
  // If the user pasted a full <iframe> embed, pull out the src
  const iframeMatch = raw.match(/src=["']([^"']+)["']/i);
  if (iframeMatch) raw = iframeMatch[1];
  try {
    const u = new URL(raw);
    if (u.hostname.includes("spotify.com")) {
      // Rebuild a clean embed URL without share/utm params that can crash the embed
      let parts = u.pathname.split("/").filter(Boolean);
      if (parts[0] === "embed") parts = parts.slice(1);
      if (parts.length >= 2) return `https://open.spotify.com/embed/${parts[0]}/${parts[1]}`;
    }
    // Already an embed URL (non-Spotify) — use as-is
    if (u.pathname.includes("/embed")) return raw;
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (u.hostname.includes("music.apple.com")) return raw.replace("music.apple.com", "embed.music.apple.com");
  } catch (e) {}
  return null;
}

function formatBirthday(value) {
  if (!value) return "";
  // date-only strings (YYYY-MM-DD) must not be shifted by timezone
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
  const d = new Date(value);
  return isNaN(d) ? String(value) : d.toLocaleDateString();
}
import { toast } from "sonner";

export default function Profile() {
  const { id: routeId, handle } = useParams();
  const navigate = useNavigate();
  const { user: me, patchUser, refreshUser } = useAuth();
  const [resolvedId, setResolvedId] = useState(null);
  const [pinnedPost, setPinnedPost] = useState(null);
  const id = routeId || resolvedId;
  const isMe = me.id === id;
  const { registerProfileHandler } = useRealtime();
  const { content } = useSiteContent();
  const backgrounds = content.backgrounds?.length ? content.backgrounds : Object.entries(BACKGROUNDS).map(([key, b]) => ({ key, label: b.label, url: b.url }));
  const bgMap = Object.fromEntries(backgrounds.map((b) => [b.key, b.url]));
  const resolveBg = (key) => bgMap[key] || bgUrl(key);
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [coverDialog, setCoverDialog] = useState(false);
  const [tempCover, setTempCover] = useState(null);
  const [coverPos, setCoverPos] = useState(50);
  const [topFriends, setTopFriends] = useState([]);
  const [topDialog, setTopDialog] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [viewsOpen, setViewsOpen] = useState(false);
  const [viewsData, setViewsData] = useState(null);
  const canSeeViews = isMe && (me.is_admin || me.role === "owner" || (me.premium_status === "active" && ["gold", "den"].includes(me.premium_tier)));
  const openViews = async () => {
    setViewsOpen(true); setViewsData(null);
    try { const { data } = await api.get(`/users/${id}/views`); setViewsData(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setViewsOpen(false); }
  };
  const [allFriends, setAllFriends] = useState([]);
  const [friendSort, setFriendSort] = useState("newest");
  const [seeAllOpen, setSeeAllOpen] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [relType, setRelType] = useState("In a Relationship");
  const [relPartner, setRelPartner] = useState(null);
  const [relQuery, setRelQuery] = useState("");
  const [relResults, setRelResults] = useState([]);
  const [myFrames, setMyFrames] = useState([]);
  useEffect(() => {
    if (isMe) api.get("/users/me/frames").then(({ data }) => setMyFrames(data)).catch(() => setMyFrames([]));
  }, [isMe]);
  const chooseFrame = async (key) => {
    try { const { data } = await api.post("/users/me/frame", { frame: key }); patchUser({ profile_frame: data.profile_frame, profile_frame_url: data.profile_frame_url }); toast.success(key ? "Frame applied!" : "Frame removed"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  useEffect(() => {
    if (!relQuery.trim()) { setRelResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(relQuery)}`); setRelResults(data.filter((u) => u.id !== me?.id)); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [relQuery]);
  const [reportOpen, setReportOpen] = useState(false);
  const [myFriends, setMyFriends] = useState([]);
  const [selectedTop, setSelectedTop] = useState([]);
  const [showSong, setShowSong] = useState(false);
  const [autoplaySong, setAutoplaySong] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoMeta, setPhotoMeta] = useState({ count: 0, limit: 30, privacy: "public", restricted: false });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);
  const photoRef = useRef();
  const [videos, setVideos] = useState([]);
  const [videoMeta, setVideoMeta] = useState({ count: 0, limit: 10, privacy: "public", restricted: false, max_seconds: 300 });
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [viewVideo, setViewVideo] = useState(null);
  const videoRef = useRef();
  const [wallPosts, setWallPosts] = useState([]);
  const avatarRef = useRef();
  const coverRef = useRef();

  const load = async () => {
    const { data } = await api.get(`/users/${id}`);
    setProfile(data);
    setForm({ name: data.name, preferred_name: data.about_me?.preferred_name || "", vanity: data.vanity || "", bio: data.bio, background: data.background, guardian_username: "",
      location: data.about_me?.location || "", relationship_status: data.relationship_status || data.about_me?.relationship_status || "",
      sexuality: data.about_me?.sexuality || "",
      song_url: data.song_url || "", song_autoplay: !!data.song_autoplay, privacy: data.privacy || {},
      social_links: data.social_links || [],
      partner_username: "", relationship_date: "", post_public: false });
    setCoverPos(data.cover_pos ?? 50);
    const pr = await api.get(`/posts/user/${id}`);
    setPosts(pr.data);
    const tf = await api.get(`/users/${id}/top-friends`);
    setTopFriends(tf.data);
    const fr2 = await api.get(`/friends?user_id=${id}`);
    setAllFriends(fr2.data);
    const ph = await api.get(`/users/${id}/photos`);
    setPhotos(ph.data.photos || []);
    setPhotoMeta({ count: ph.data.count || 0, limit: ph.data.limit || 30, privacy: ph.data.privacy || "public", restricted: !!ph.data.restricted });
    const vd = await api.get(`/users/${id}/videos`);
    setVideos(vd.data.videos || []);
    setVideoMeta({ count: vd.data.count || 0, limit: vd.data.limit || 10, privacy: vd.data.privacy || "public", restricted: !!vd.data.restricted, max_seconds: vd.data.max_seconds || 300 });
    const wl = await api.get(`/users/${id}/wall`);
    setWallPosts(wl.data);
  };
  useEffect(() => { if (id) load(); }, [id]);

  // Resolve /u/:handle (vanity URL) to a user id, keeping the pretty URL in the address bar.
  useEffect(() => {
    if (routeId || !handle) return;
    api.get(`/users/resolve/${encodeURIComponent(handle)}`)
      .then(({ data }) => setResolvedId(data.id))
      .catch(() => { toast.error("We couldn't find that Spotter page."); navigate("/feed", { replace: true }); });
  }, [handle, routeId, navigate]);

  const loadPinned = () => { if (id) api.get(`/users/${id}/pinned-post`).then(({ data }) => setPinnedPost(data)).catch(() => setPinnedPost(null)); };
  useEffect(() => { loadPinned(); }, [id]);

  // Real-time profile sync: patch this profile when the owner edits it on another device.
  useEffect(() => {
    const off = registerProfileHandler((u) => {
      if (!u || u.id !== id) return;
      setProfile((p) => (p ? { ...p, ...u } : p));
      api.get(`/users/${id}/top-friends`).then((r) => setTopFriends(r.data)).catch(() => {});
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Keep this tab in sync with edits made on other devices: refetch when the tab regains focus.
  const editRef = useRef(false);
  useEffect(() => { editRef.current = edit; }, [edit]);
  // Arm the profile song to start on the visitor's first interaction (browsers block silent autoplay).
  useEffect(() => {
    setAutoplaySong(false);
    const evs = ["click", "keydown", "touchstart"];
    const arm = () => { setAutoplaySong(true); evs.forEach((e) => document.removeEventListener(e, arm)); };
    evs.forEach((e) => document.addEventListener(e, arm));
    return () => evs.forEach((e) => document.removeEventListener(e, arm));
  }, [id]);

  // Paint the whole page (behind header/footer) with this profile's background.
  useEffect(() => {
    const frame = document.querySelector(".leopard-frame");
    if (!frame) return;
    const bg = profile?.background ? `url(${resolveBg(profile.background)})` : null;
    if (bg) frame.style.backgroundImage = bg;
    return () => { frame.style.backgroundImage = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.background]);
  useEffect(() => {
    const refetch = () => {
      if (document.visibilityState === "visible" && !editRef.current) { load(); refreshUser?.(); }
    };
    window.addEventListener("focus", refetch);
    document.addEventListener("visibilitychange", refetch);
    return () => { window.removeEventListener("focus", refetch); document.removeEventListener("visibilitychange", refetch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!profile) return <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-spotter-maroon" /></div>;

  const uploadFile = async (file) => {
    const prepared = await processImage(file);
    const fd = new FormData();
    fd.append("file", prepared);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const onAvatar = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setAvatarFile(f); setCropperOpen(true);
    e.target.value = "";
  };
  const editCurrentPhoto = async () => {
    if (!profile.avatar_url) return;
    try {
      const res = await fetch(mediaUrl(profile.avatar_url), { mode: "cors" });
      const blob = await res.blob();
      setAvatarFile(new File([blob], "current.jpg", { type: blob.type || "image/jpeg" }));
      setCropperOpen(true);
    } catch (err) {
      toast.error("Couldn't load your current photo for editing. Try uploading a new one.");
    }
  };
  const onAvatarCropped = async (croppedFile) => {
    try { const url = await uploadFile(croppedFile); await api.put("/users/me", { avatar_url: url }); patchUser({ avatar_url: url }); load(); toast.success("Avatar updated"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const onPhotoPick = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setPhotoUploading(true);
    try {
      let added = 0;
      for (const f of files) {
        if (photoMeta.count + added >= photoMeta.limit) { toast.info(`Reached the ${photoMeta.limit}-photo limit.`); break; }
        const url = await uploadFile(f);
        const { data } = await api.post("/users/me/photos", { url });
        setPhotos((prev) => [...prev, data.photo]);
        setPhotoMeta((m) => ({ ...m, count: data.count }));
        added++;
      }
      if (added) toast.success(added === 1 ? "Photo added" : `${added} photos added`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPhotoUploading(false); e.target.value = ""; }
  };
  const deletePhoto = async (photoId) => {
    try { await api.delete(`/users/me/photos/${photoId}`); setPhotos((prev) => prev.filter((p) => p.id !== photoId)); setPhotoMeta((m) => ({ ...m, count: Math.max(0, m.count - 1) })); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const togglePhotoPrivacy = async () => {
    const next = photoMeta.privacy === "public" ? "friends" : "public";
    try { await api.put("/users/me", { photos_privacy: next }); setPhotoMeta((m) => ({ ...m, privacy: next })); toast.success(next === "friends" ? "Photos are now Pride only" : "Photos are now public"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleTagApproval = async () => {
    const next = !me?.photo_tag_approval;
    try { await api.put("/users/me", { photo_tag_approval: next }); patchUser({ photo_tag_approval: next }); toast.success(next ? "You'll approve photo tags before they show" : "Photo tags will appear automatically"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const probeDuration = (file) => new Promise((resolve) => {
    try {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => { const d = v.duration; URL.revokeObjectURL(v.src); resolve(isFinite(d) ? d : null); };
      v.onerror = () => resolve(null);
      v.src = URL.createObjectURL(file);
    } catch (e) { resolve(null); }
  });
  const onVideoPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    e.target.value = "";
    if (!f.type.startsWith("video/")) { toast.error("Please choose a video file."); return; }
    const duration = await probeDuration(f);
    if (duration && duration > videoMeta.max_seconds + 1) {
      toast.error(`Videos must be 5 minutes or shorter. This one is ${Math.floor(duration / 60)}m ${Math.round(duration % 60)}s.`);
      return;
    }
    setVideoUploading(true); setVideoProgress(0);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => { if (p.total) setVideoProgress(Math.round((p.loaded / p.total) * 100)); },
      });
      const res = await api.post("/users/me/videos", { url: data.url, duration });
      setVideos((prev) => [...prev, res.data.video]);
      setVideoMeta((m) => ({ ...m, count: res.data.count }));
      toast.success("Video added");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setVideoUploading(false); setVideoProgress(0); }
  };
  const deleteVideo = async (videoId) => {
    try { await api.delete(`/users/me/videos/${videoId}`); setVideos((prev) => prev.filter((v) => v.id !== videoId)); setVideoMeta((m) => ({ ...m, count: Math.max(0, m.count - 1) })); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const toggleVideoPrivacy = async () => {
    const next = videoMeta.privacy === "public" ? "friends" : "public";
    try { await api.put("/users/me", { videos_privacy: next }); setVideoMeta((m) => ({ ...m, privacy: next })); toast.success(next === "friends" ? "Videos are now Pride only" : "Videos are now public"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const toggleWallApproval = async () => {
    const next = !profile.wall_approval;
    try { await api.put("/users/me", { wall_approval: next }); patchUser({ wall_approval: next }); setProfile((p) => ({ ...p, wall_approval: next })); toast.success(next ? "Scratch Post now requires your approval" : "Scratch Post is now open to all your Pride"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const fmtDur = (s) => { if (!s && s !== 0) return ""; const m = Math.floor(s / 60); const sec = Math.round(s % 60); return `${m}:${String(sec).padStart(2, "0")}`; };

  const onCoverPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const url = await uploadFile(f); setTempCover(url); setCoverPos(50); setCoverDialog(true); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const saveCover = async () => {
    try {
      await api.put("/users/me", { cover_url: tempCover, cover_pos: coverPos });
      setCoverDialog(false); setTempCover(null); load(); toast.success("Cover photo set");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Couldn't save your cover photo. Please try again."); }
  };

  const saveProfile = async () => {
    try {
      const payload = { name: form.name, preferred_name: form.preferred_name, vanity: form.vanity, bio: form.bio, background: form.background,
        location: form.location, relationship_status: form.relationship_status, sexuality: form.sexuality, song_url: form.song_url, song_autoplay: form.song_autoplay, privacy: form.privacy,
        social_links: (form.social_links || []).filter((s) => s.url && s.url.trim()) };
      if (form.guardian_username?.trim()) payload.guardian_username = form.guardian_username.trim();
      const { data } = await api.put("/users/me", payload);
      patchUser(data); setEdit(false); load(); toast.success("Profile saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const requestRelationship = async () => {
    if (!relPartner) return;
    try { await api.post("/relationship/request", { partner_id: relPartner.id, type: relType, date: form.relationship_date || null, post_public: form.post_public });
      setEdit(false); setRelPartner(null); setRelQuery(""); load(); toast.success(`${relType} request sent — awaiting their approval`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const respondRelationship = async (action) => {
    try { await api.post("/relationship/respond", { request_id: "x", action }); load(); toast.success(action === "approve" ? "Relationship confirmed 💛" : "Request declined"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const removeRelationship = async () => {
    try { await api.post("/relationship/remove"); load(); toast.success("Relationship removed"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const doFriend = async () => {
    try { const { data } = await api.post("/friends/request", { to_user_id: id });
      toast.success(data.guardian_status === "pending" ? "Request sent — awaiting guardian approval" : "Pride request sent"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const respond = async (action) => {
    try { await api.post("/friends/respond", { request_id: profile.friendship.request_id, action }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const block = async () => { await api.post("/friends/block", { to_user_id: id }); toast.success("User blocked"); load(); };

  const openTopEditor = async () => {
    const { data } = await api.get("/friends");
    setMyFriends(data);
    setSelectedTop(topFriends.map((u) => u.id));
    setTopDialog(true);
  };
  const toggleTop = (uid) => setSelectedTop((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : (prev.length >= 12 ? prev : [...prev, uid]));
  const moveTop = (from, to) => setSelectedTop((prev) => { if (to < 0 || to >= prev.length) return prev; const a = [...prev]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; });
  const saveTop = async () => {
    try { await api.put("/users/me/top-friends", { ids: selectedTop }); setTopDialog(false); load(); toast.success("Top Spots updated"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleHideFriends = async (v) => {
    try { await api.put("/users/me", { hide_friends: v }); patchUser({ hide_friends: v }); setProfile((p) => ({ ...p, hide_friends: v })); toast.success(v ? "Pride hidden" : "Pride visible"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const twoCol = !!profile.classic_layout;
  const songEmbed = profile.song_url ? toMusicEmbed(profile.song_url) : null;
  const songSrc = songEmbed && autoplaySong && profile.song_autoplay ? songEmbed + (songEmbed.includes("?") ? "&" : "?") + "autoplay=1" : songEmbed;
  const topIds = topFriends.map((u) => u.id);
  const restFriends = allFriends;
  const sortedRest = [...restFriends].sort((a, b) =>
    friendSort === "az" ? a.name.localeCompare(b.name) : (b.friend_since || "").localeCompare(a.friend_since || ""));

  const fs = profile.friendship || { state: "none" };

  return (
    <div data-testid="profile-root">
      <div style={{ backgroundImage: `url(${resolveBg(profile.background)})` }} className="profile-bg p-4 sm:p-6">
        <div className={twoCol ? "max-w-7xl mx-auto" : "max-w-3xl mx-auto"}>
        <div className="bg-white border-4 border-black rounded-2xl brand-shadow">
          {/* Cover */}
          <div className="relative w-full bg-spotter-cream overflow-hidden rounded-t-xl" style={{ aspectRatio: twoCol ? "16/6" : "16/9", maxHeight: twoCol ? undefined : 280 }}>
            {profile.cover_url ? (
              <img src={mediaUrl(profile.cover_url)} alt="cover" data-testid="view-cover-btn" onClick={() => setViewPhoto({ url: profile.cover_url })} className="w-full h-full object-cover cursor-pointer" style={{ objectPosition: `center ${profile.cover_pos ?? 50}%` }} />
            ) : (
              <div className="w-full h-full" style={{ backgroundImage: `url(${resolveBg(profile.background)})`, backgroundSize: "cover" }} />
            )}
            {isMe && (
              <>
                <input ref={coverRef} type="file" accept="image/*" hidden onChange={onCoverPick} />
                <Button data-testid="edit-cover-btn" onClick={() => coverRef.current?.click()} size="sm"
                  className="absolute bottom-3 right-3 bg-black/70 text-white border border-white gap-2"><Camera className="w-4 h-4" />Cover photo</Button>
              </>
            )}
          </div>

          {/* Header */}
          <div className="px-5 pb-5">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
              <div className={`relative -mt-12 self-start ${profile.profile_frame === "den" ? "mb-12 sm:mr-11" : profile.profile_frame ? "mb-8 sm:mr-8" : ""}`}>
                <button type="button" data-testid="view-avatar-btn" onClick={() => profile.avatar_url && setViewPhoto({ url: profile.avatar_url })}
                  className={`rounded-full ${profile.avatar_url ? "cursor-pointer" : "cursor-default"}`} aria-label="View profile picture">
                  <Avatar user={profile} size="xl" showPresence className="ring-4 ring-spotter-cream rounded-full" />
                </button>
                {isMe && (
                  <>
                    <input ref={avatarRef} type="file" accept="image/*" hidden onChange={onAvatar} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid="edit-avatar-btn" className="absolute bottom-1 right-1 bg-spotter-maroon text-spotter-cream rounded-full p-2 border-2 border-black"><Camera className="w-4 h-4" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="border-2 border-black bg-white">
                        <DropdownMenuItem data-testid="avatar-upload-new" onClick={() => avatarRef.current?.click()}><Camera className="w-4 h-4 mr-2" />Upload new photo</DropdownMenuItem>
                        {profile.avatar_url && <DropdownMenuItem data-testid="avatar-edit-current" onClick={editCurrentPhoto}><Pencil className="w-4 h-4 mr-2" />Edit current photo</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
              <div className="flex-1 pb-2 min-w-0">
                <h2 className="font-display text-3xl font-bold text-spotter-maroon break-words">{profile.name}{profile.about_me?.legal_name ? <span className="font-heading text-xl text-spotter-maroon/60"> ({profile.about_me.legal_name})</span> : null}</h2>
                <div className="text-sm text-spotter-maroon flex items-center gap-2 flex-wrap">
                  <span>@{profile.username}</span>
                  <span data-testid="visitor-counter" title="Profile visits" className="inline-flex items-center gap-1 text-xs bg-spotter-gold/30 border border-black rounded-full px-2 py-0.5"><Eye className="w-3 h-3" />{profile.visit_count || 0} visits</span>
                  {profile.requires_guardian && <span className="inline-flex items-center gap-1 text-xs bg-spotter-gold/30 border border-black rounded-full px-2"><ShieldCheck className="w-3 h-3" />Guardian-protected</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pb-2 w-full sm:w-auto">
                {isMe ? (
                  <>
                    {canSeeViews && (
                      <Button data-testid="profile-views-btn" onClick={openViews} variant="outline" className="border-2 border-black gap-1 w-full sm:w-auto"><Eye className="w-4 h-4" />Views{profile?.visit_count ? ` (${profile.visit_count})` : ""}</Button>
                    )}
                    <Button data-testid="edit-profile-btn" onClick={() => setEdit(true)} className="bg-spotter-maroon text-spotter-cream border-2 border-black w-full sm:w-auto">Edit Profile</Button>
                  </>
                ) : (
                  <>
                    {fs.state === "friends" && <span className="inline-flex items-center gap-1 px-3 py-2 border-2 border-black rounded-md bg-spotter-cream font-semibold"><UserCheck className="w-4 h-4" />In your Pride</span>}
                    {fs.state === "none" && <Button data-testid="add-friend-btn" onClick={doFriend} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-2"><UserPlus className="w-4 h-4" />Add to Pride</Button>}
                    {fs.state === "pending" && fs.direction === "outgoing" && <span className="inline-flex items-center gap-1 px-3 py-2 border-2 border-black rounded-md bg-spotter-cream"><Clock className="w-4 h-4" />{fs.guardian_status === "pending" ? "Awaiting guardian" : "Requested"}</span>}
                    {fs.state === "pending" && fs.direction === "incoming" && (
                      <>
                        <Button data-testid="accept-friend-btn" onClick={() => respond("accept")} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" />Accept</Button>
                        <Button data-testid="decline-friend-btn" onClick={() => respond("decline")} variant="outline" className="border-2 border-black gap-1"><X className="w-4 h-4" />Decline</Button>
                      </>
                    )}
                    <Button data-testid="message-btn" onClick={() => navigate(`/messages/${id}`)} variant="outline" className="border-2 border-black gap-2"><MessageCircle className="w-4 h-4" />Message</Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button data-testid="profile-more-btn" className="p-2 border-2 border-black rounded-md bg-white"><MoreHorizontal className="w-5 h-5" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="border-2 border-black bg-white">
                        <DropdownMenuItem data-testid="block-user-btn" onClick={block} className="text-red-700"><Ban className="w-4 h-4 mr-2" />Block</DropdownMenuItem>
                        <DropdownMenuItem data-testid="report-user-btn" onClick={() => setReportOpen(true)}><Flag className="w-4 h-4 mr-2" />Report</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
            </div>
            {profile.bio && <p className="mt-3 text-[15px]">{profile.bio}</p>}
          </div>
        </div>

        {profile.is_private ? (
          <div className="bg-white border-4 border-black rounded-2xl mt-6 p-10 text-center brand-shadow" data-testid="private-profile-notice">
            <Lock className="w-10 h-10 mx-auto text-spotter-maroon mb-3" />
            <h3 className="font-display text-2xl font-bold text-spotter-maroon">This profile is private</h3>
            <p className="text-sm text-spotter-maroon mt-2 max-w-md mx-auto">Only {profile.name}'s Pride can see their photos, posts and details. Send a Pride request to connect.</p>
          </div>
        ) : (
        <>
        <div className={twoCol ? "lg:grid lg:grid-cols-2 lg:gap-x-16 lg:gap-y-0 lg:items-start" : "flex flex-col"}>
        <div className={twoCol ? "min-w-0" : "contents"}>
        {/* About Me (directly under bio) */}
        <div className={`bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow ${twoCol ? "" : "order-1"}`} data-testid="about-me-section">
          <div>
            <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3 flex items-center gap-2"><Crown className="w-6 h-6" style={{ color: "#E19C00" }} />About Me</h3>
            {(() => {
              const a = profile.about_me || {};
              const rel = a.relationship;
              const pendingForMe = rel && rel.status === "pending" && rel.partner?.id === me.id;
              return (
                <>
                  {pendingForMe && (
                    <div className="mb-3 bg-spotter-gold/20 border-2 border-black rounded-lg p-3" data-testid="relationship-request-banner">
                      <p className="text-sm font-semibold mb-2">{profile.name} asked to be <b>{rel.type || "In a Relationship"}</b> with you.</p>
                      <div className="flex gap-2">
                        <Button data-testid="relationship-approve" size="sm" onClick={() => respondRelationship("approve")} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" />Approve</Button>
                        <Button data-testid="relationship-deny" size="sm" variant="outline" onClick={() => respondRelationship("deny")} className="border-2 border-black gap-1"><X className="w-4 h-4" />Deny</Button>
                      </div>
                    </div>
                  )}
                  <ul className="space-y-2 text-[15px]">
                    {a.preferred_name && <li className="flex items-center gap-2" data-testid="about-preferred-name"><UserCheck className="w-4 h-4 text-spotter-maroon" />Preferred name: <b>{a.preferred_name}</b></li>}
                    {a.birthday && <li className="flex items-center gap-2"><Cake className="w-4 h-4 text-spotter-maroon" />Birthday: <b>{formatBirthday(a.birthday)}</b></li>}
                    {a.location && <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-spotter-maroon" />Location: <b>{a.location}</b></li>}
                    {a.zodiac && <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-spotter-maroon" />Zodiac: <b>{a.zodiac}</b></li>}
                    {a.gender && <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-spotter-maroon" />Gender: <b>{a.gender}</b></li>}
                    {a.sexuality && <li className="flex items-center gap-2"><Heart className="w-4 h-4 text-spotter-maroon" />Sexuality: <b>{a.sexuality}</b></li>}
                    {a.relationship_status && (
                      <li className="flex items-center gap-2 flex-wrap"><Heart className="w-4 h-4 text-spotter-maroon" />{a.relationship_status}
                        {rel?.partner && <button onClick={() => navigate(`/profile/${rel.partner.id}`)} className="font-bold underline">with {rel.partner.name}</button>}
                        {rel?.status === "confirmed" && rel?.date && <span className="text-spotter-maroon">since {new Date(rel.date + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>}
                      </li>
                    )}
                    {a.joined && <li className="text-xs text-spotter-maroon mt-1">On Spotter since {new Date(a.joined).toLocaleDateString()}</li>}
                  </ul>
                  {profile.social_links?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="social-links">
                      {profile.social_links.map((s, i) => (
                        <a key={i} href={/^https?:\/\//.test(s.url) ? s.url : `https://${s.url}`} target="_blank" rel="noreferrer" data-testid={`social-link-${i}`}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-spotter-maroon bg-spotter-cream border-2 border-black rounded-full px-3 py-1 hover:bg-spotter-gold/30 transition-colors">
                          <Globe className="w-3.5 h-3.5" />{s.platform || "Link"}
                        </a>
                      ))}
                    </div>
                  )}
                  {!profile.can_view_private && <p className="text-xs text-spotter-maroon mt-2 italic">Some details are private.</p>}
                  {isMe && rel && rel.status === "confirmed" && <Button data-testid="remove-relationship" size="sm" variant="outline" onClick={removeRelationship} className="mt-3 border-2 border-black">Remove relationship</Button>}
                </>
              );
            })()}
          </div>
        </div>

        {(() => {
          const canPlaylist = profile.role === "owner" || profile.premium_status === "active";
          const showPlaylist = profile.role === "owner" || (canPlaylist && profile.playlist_id);
          if (showPlaylist) return <ProfilePlaylist playlistId={profile.playlist_id || undefined} />;
          if (profile.song_url) return (
            <div className="bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow" data-testid="my-song-section">
              <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3 flex items-center gap-2"><Music className="w-6 h-6" style={{ color: "#E19C00" }} />My Song</h3>
              {songEmbed && !edit ? (
                <iframe data-testid="song-embed" src={songSrc} title="song" className="w-full rounded-lg border-2 border-black" height="152" allow="autoplay; encrypted-media" loading="lazy" />
              ) : songEmbed && edit ? (
                <div className="w-full rounded-lg border-2 border-black bg-white/60 flex items-center justify-center text-sm text-spotter-maroon" style={{ height: 152 }} data-testid="song-embed-paused">Song paused while editing…</div>
              ) : (
                <a href={profile.song_url} target="_blank" rel="noreferrer" className="text-spotter-maroon underline font-semibold">Listen to my song</a>
              )}
            </div>
          );
          if (isMe) return (
            <div className="bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow" data-testid="my-song-section">
              <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3 flex items-center gap-2"><Music className="w-6 h-6" style={{ color: "#E19C00" }} />{canPlaylist ? "My Playlist" : "My Song"}</h3>
              <p className="text-sm text-spotter-maroon">{canPlaylist ? "Add your YouTube playlist from Edit Profile to show a shuffle player here." : "Add your favourite song from Edit Profile."}</p>
            </div>
          );
          return null;
        })()}


        {/* Status + Scratch Post relocated (status under Videos, Scratch Post under Pride) */}

        {/* About Me relocated directly under bio */}

        {/* Top Spots (left column) */}
        <div className={`bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow ${twoCol ? "" : "order-5"}`} data-testid="top-friends-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2"><Star className="w-6 h-6" style={{ color: "#E19C00" }} />Top Spots</h3>
            {isMe && <Button data-testid="edit-top-friends-btn" size="sm" onClick={openTopEditor} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Pencil className="w-4 h-4" />Edit Top Spots</Button>}
          </div>
          {topFriends.length === 0 ? (
            <p className="text-sm text-spotter-maroon">{isMe ? "Pick your Top Spots to show off your favourite Pride here." : "No Top Spots picked yet."}</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
              {topFriends.map((u, i) => (
                <button key={u.id} data-testid={`top-friend-${u.id}`} onClick={() => navigate(`/profile/${u.id}`)}
                  className="flex flex-col items-center gap-3 p-3 pt-4 rounded-xl border-2 border-black bg-spotter-cream hover:bg-white relative">
                  <span className="absolute top-1 left-1 text-[10px] font-bold bg-spotter-gold border border-black rounded-full w-5 h-5 flex items-center justify-center">{i + 1}</span>
                  <Avatar user={u} size="lg" showPresence />
                  <span className="text-xs font-bold text-center truncate w-full">{u.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Rest of friends (left column) */}
        {(isMe || !profile.hide_friends) && (
          <div className={`bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow ${twoCol ? "" : "order-6"}`} data-testid="rest-friends-section">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2"><Users className="w-6 h-6" style={{ color: "#E19C00" }} />Pride <span className="text-base font-bold text-spotter-maroon/70">({restFriends.length})</span></h3>
              <div className="flex items-center gap-2">
                {restFriends.length > 0 && (
                  <button data-testid="friend-sort-toggle" onClick={() => setFriendSort((s) => s === "newest" ? "az" : "newest")}
                    className="inline-flex items-center gap-1 text-xs font-bold border-2 border-black rounded-full px-3 py-1.5 bg-spotter-cream hover:bg-white">
                    <ArrowUpDown className="w-3.5 h-3.5" />{friendSort === "newest" ? "Newest" : "A–Z"}
                  </button>
                )}
                {isMe && (
                  <button data-testid="hide-friends-toggle" onClick={() => toggleHideFriends(!profile.hide_friends)}
                    className="inline-flex items-center gap-1 text-xs font-bold border-2 border-black rounded-full px-3 py-1.5 bg-spotter-cream hover:bg-white">
                    {profile.hide_friends ? <><Eye className="w-3.5 h-3.5" />Show list</> : <><EyeOff className="w-3.5 h-3.5" />Hide list</>}
                  </button>
                )}
              </div>
            </div>
            {isMe && profile.hide_friends && <p className="text-xs text-spotter-maroon mb-2 italic">Your friends list is hidden from others. Only you can see it.</p>}
            {restFriends.length === 0 ? (
              <p className="text-sm text-spotter-maroon">{isMe ? "Add more Pride to grow your crew." : "No other Pride to show."}</p>
            ) : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                  {sortedRest.slice(0, 12).map((u) => (
                    <button key={u.id} data-testid={`rest-friend-${u.id}`} onClick={() => navigate(`/profile/${u.id}`)}
                      className="flex flex-col items-center gap-3 p-3 pt-4 rounded-xl border-2 border-black bg-spotter-cream hover:bg-white">
                      <Avatar user={u} size="lg" showPresence />
                      <span className="text-xs font-bold text-center truncate w-full">{u.name}</span>
                    </button>
                  ))}
                </div>
                {restFriends.length > 12 && (
                  <Button data-testid="see-all-friends-btn" onClick={() => setSeeAllOpen(true)} variant="outline" className="mt-3 border-2 border-black gap-2"><Users className="w-4 h-4" />See all {restFriends.length} Pride</Button>
                )}
              </>
            )}
          </div>
        )}

        {/* Scratch Post (left column) */}
        <div className={`bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow ${twoCol ? "" : "order-7"}`} data-testid="wall-section">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h3 className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2"><MessageSquare className="w-6 h-6" style={{ color: "#E19C00" }} />The Scratch Post</h3>
            {isMe && (
              <button data-testid="wall-approval-toggle" onClick={toggleWallApproval}
                className="inline-flex items-center gap-1 text-xs font-bold border-2 border-black rounded-full px-3 py-1.5 bg-spotter-cream hover:bg-white">
                {profile.wall_approval ? <><ShieldCheck className="w-3.5 h-3.5" />Approve posts first</> : <><Check className="w-3.5 h-3.5" />Allow all Pride</>}
              </button>
            )}
          </div>
          {fs.state === "friends" ? (
            <WallComposer wallOwnerId={id} ownerName={profile.name} isOwner={isMe}
              onPosted={(p) => { setWallPosts((prev) => [p, ...prev]); }} />
          ) : isMe ? (
            <p className="text-sm text-spotter-maroon mb-3">Only your Pride can post on your Scratch Post — it's their space to leave you love. 🐾</p>
          ) : (
            <p className="text-sm text-spotter-maroon mb-3">Add {profile.name} to your Pride to post on their Scratch Post.</p>
          )}
          {wallPosts.length === 0 ? (
            <p className="text-sm text-spotter-maroon">{isMe ? "Your Scratch Post is empty. Your Pride can post here!" : "Nothing on the Scratch Post yet — be the first!"}</p>
          ) : (
            wallPosts.map((p) => (
              <PostCard key={p.id} post={p}
                onDeleted={(pid) => setWallPosts((prev) => prev.filter((x) => x.id !== pid))}
                onModerated={(pid) => setWallPosts((prev) => prev.map((x) => x.id === pid ? { ...x, wall_status: "approved" } : x))} />
            ))
          )}
        </div>
        </div>
        <div className={twoCol ? "min-w-0" : "contents"}>
        {/* Photos */}
        {(isMe || !photoMeta.restricted) && (
          <div className={`bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow ${twoCol ? "" : "order-2"}`} data-testid="photos-section">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2">
                <ImagePlus className="w-6 h-6" style={{ color: "#E19C00" }} />Photos
                <span className="text-base font-bold text-spotter-maroon/70">({photoMeta.count}/{photoMeta.limit})</span>
              </h3>
              {isMe && (
                <div className="flex items-center gap-2">
                  <button data-testid="photos-privacy-toggle" onClick={togglePhotoPrivacy}
                    className="inline-flex items-center gap-1 text-xs font-bold border-2 border-black rounded-full px-3 py-1.5 bg-spotter-cream hover:bg-white">
                    {photoMeta.privacy === "friends" ? <><Lock className="w-3.5 h-3.5" />Pride only</> : <><Globe className="w-3.5 h-3.5" />Public</>}
                  </button>
                  <button data-testid="photo-tag-approval-toggle" onClick={toggleTagApproval}
                    className="inline-flex items-center gap-1 text-xs font-bold border-2 border-black rounded-full px-3 py-1.5 bg-spotter-cream hover:bg-white">
                    <Tag className="w-3.5 h-3.5" />{me?.photo_tag_approval ? "Approve tags" : "Auto tags"}
                  </button>
                  <input ref={photoRef} type="file" accept="image/*" multiple hidden onChange={onPhotoPick} />
                  <Button data-testid="add-photo-btn" size="sm" disabled={photoUploading || photoMeta.count >= photoMeta.limit}
                    onClick={() => photoRef.current?.click()} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1">
                    {photoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Add photo
                  </Button>
                </div>
              )}
            </div>
            {isMe && photoMeta.count >= photoMeta.limit && (
              <p className="text-xs text-spotter-maroon mb-2 italic">You've reached the {photoMeta.limit}-photo limit. Premium members will be able to add more soon. 🐆</p>
            )}
            {photos.length === 0 ? (
              <p className="text-sm text-spotter-maroon">{isMe ? "Add up to 30 photos to show off your world." : "No photos yet."}</p>
            ) : (
              <>
                <div className={`grid grid-cols-3 ${twoCol ? "sm:grid-cols-3" : "sm:grid-cols-5"} gap-3`}>
                  {(showAllPhotos ? photos : photos.slice(0, twoCol ? 6 : 5)).map((p) => (
                    <div key={p.id} data-testid={`photo-${p.id}`} className="relative group aspect-square rounded-xl border-2 border-black overflow-hidden bg-spotter-cream">
                      <img src={mediaUrl(p.url)} alt="" onClick={() => setViewPhoto(p)} className="w-full h-full object-cover cursor-pointer" />
                      {isMe && (
                        <button data-testid={`delete-photo-${p.id}`} onClick={() => deletePhoto(p.id)}
                          className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  ))}
                </div>
                {photos.length > (twoCol ? 6 : 5) && (
                  <Button data-testid="photos-see-all" onClick={() => setShowAllPhotos((v) => !v)} variant="outline" className="mt-3 border-2 border-black gap-2">
                    <ImagePlus className="w-4 h-4" />{showAllPhotos ? "Show less" : `See all ${photos.length} photos`}
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {/* Videos */}
        {(isMe || !videoMeta.restricted) && (
          <div className={`bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow ${twoCol ? "" : "order-3"}`} data-testid="videos-section">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2">
                <Video className="w-6 h-6" style={{ color: "#E19C00" }} />Videos
                <span className="text-base font-bold text-spotter-maroon/70">({videoMeta.count}/{videoMeta.limit})</span>
              </h3>
              {isMe && (
                <div className="flex items-center gap-2">
                  <button data-testid="videos-privacy-toggle" onClick={toggleVideoPrivacy}
                    className="inline-flex items-center gap-1 text-xs font-bold border-2 border-black rounded-full px-3 py-1.5 bg-spotter-cream hover:bg-white">
                    {videoMeta.privacy === "friends" ? <><Lock className="w-3.5 h-3.5" />Pride only</> : <><Globe className="w-3.5 h-3.5" />Public</>}
                  </button>
                  <input ref={videoRef} type="file" accept="video/*" hidden onChange={onVideoPick} />
                  <Button data-testid="add-video-btn" size="sm" disabled={videoUploading || videoMeta.count >= videoMeta.limit}
                    onClick={() => videoRef.current?.click()} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1">
                    {videoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Add video
                  </Button>
                </div>
              )}
            </div>
            {isMe && videoUploading && (
              <div className="mb-3" data-testid="video-upload-progress">
                <div className="h-2 w-full bg-spotter-cream border-2 border-black rounded-full overflow-hidden">
                  <div className="h-full bg-spotter-maroon transition-all" style={{ width: `${videoProgress}%` }} />
                </div>
                <p className="text-xs text-spotter-maroon mt-1">Uploading… {videoProgress}%</p>
              </div>
            )}
            {isMe && (<p className="text-xs text-spotter-maroon mb-2 italic">Up to {videoMeta.limit} videos, max 5 minutes each. Want to share a YouTube, TikTok or Facebook video instead? Paste the link in a post on The Prowl.</p>)}
            {isMe && videoMeta.count >= videoMeta.limit && (
              <p className="text-xs text-spotter-maroon mb-2 italic">You've reached the {videoMeta.limit}-video limit. Premium members will be able to add more soon. 🐆</p>
            )}
            {videos.length === 0 ? (
              <p className="text-sm text-spotter-maroon">{isMe ? "Add up to 10 short videos (5 min each) to your page." : "No videos yet."}</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {videos.map((v) => (
                  <div key={v.id} data-testid={`video-${v.id}`} className="relative group aspect-video rounded-xl border-2 border-black overflow-hidden bg-black">
                    <video src={mediaUrl(v.url)} preload="metadata" className="w-full h-full object-cover" />
                    <button onClick={() => setViewVideo(v)} className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/40 transition-colors">
                      <span className="bg-spotter-maroon/90 text-spotter-cream rounded-full p-3 border-2 border-white"><Play className="w-5 h-5" /></span>
                    </button>
                    {v.duration != null && <span className="absolute bottom-1 right-1 text-[10px] font-bold bg-black/75 text-white rounded px-1.5 py-0.5">{fmtDur(v.duration)}</span>}
                    {isMe && (
                      <button data-testid={`delete-video-${v.id}`} onClick={() => deleteVideo(v.id)}
                        className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Status composer (own profile) — under Videos */}
        {isMe && (
          <div className={`mt-6 ${twoCol ? "" : "order-4"}`}>
            <StatusComposer onPosted={(p) => { if (p) setPosts((prev) => [p, ...prev]); load(); }} />
            <div className="mt-3 flex justify-center"><InviteButton label="Invite friends — earn spots 🐆" testid="profile-invite-btn" className="w-full" /></div>
          </div>
        )}

        {pinnedPost && (
          <div className={`max-w-2xl mx-auto mt-6 ${twoCol ? "" : "order-6"}`} data-testid="profile-pinned-post">
            <h3 className="font-display text-xl font-bold text-spotter-maroon mb-3 px-1 flex items-center gap-1"><Pin className="w-4 h-4" style={{ color: "#E19C00" }} />Pinned status</h3>
            <PostCard post={pinnedPost} onDeleted={() => { setPinnedPost(null); load(); }} onPinnedChange={loadPinned} />
          </div>
        )}

        <div className={`max-w-2xl mx-auto mt-6 ${twoCol ? "" : "order-8"}`}>
          <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3 px-1">Posts</h3>
          {posts.length === 0 ? <div className="text-center text-spotter-maroon py-6 bg-white/70 rounded-xl border-2 border-black">No posts yet.</div>
            : (showAllPosts ? posts : posts.slice(0, 3)).filter((p) => !pinnedPost || p.id !== pinnedPost.id).map((p) => <PostCard key={p.id} post={p} onPinnedChange={loadPinned} onDeleted={(pid) => setPosts((prev) => prev.filter((x) => x.id !== pid))} />)}
          {posts.length > 3 && (
            <button data-testid="posts-see-more-btn" onClick={() => setShowAllPosts((v) => !v)}
              className="mt-1 mx-auto flex items-center gap-2 text-sm font-bold text-spotter-maroon bg-white border-2 border-black rounded-full px-4 py-2 brand-shadow hover:bg-spotter-cream transition-colors">
              {showAllPosts ? "Show less" : `See more (${posts.length - 3} more)`}
            </button>
          )}
        </div>
        </div>
        </div>
        </>
        )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent className="border-4 border-black bg-spotter-cream max-h-[90dvh] overflow-y-auto overscroll-contain top-[5dvh] translate-y-0" data-testid="edit-profile-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-spotter-maroon">Edit Profile</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Display name (public)</Label><Input data-testid="edit-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-2 border-black" /></div>
            <div><Label>Preferred name (shown in About Me)</Label><Input data-testid="edit-preferred-name" value={form.preferred_name || ""} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} className="border-2 border-black" /></div>
            <div><Label>Custom profile URL</Label>
              <div className="flex items-center gap-1"><span className="text-sm text-spotter-maroon shrink-0">/u/</span><Input data-testid="edit-vanity" value={form.vanity || ""} onChange={(e) => setForm({ ...form, vanity: e.target.value })} placeholder="yourname" className="border-2 border-black" /></div>
              <p className="text-xs text-spotter-maroon mt-1">3–20 letters, numbers or underscores. Share it as thetopspotter.com/u/{form.vanity || "yourname"}</p>
            </div>
            <div><Label>Bio</Label><Textarea data-testid="edit-bio" value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="border-2 border-black" /></div>
            <div><Label>Location</Label><Input data-testid="edit-location" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Savannah, Earth" className="border-2 border-black" /></div>
            <div><Label>Relationship status</Label><Input data-testid="edit-relationship-status" value={form.relationship_status || ""} onChange={(e) => setForm({ ...form, relationship_status: e.target.value })} placeholder="Single / It's complicated…" className="border-2 border-black" /></div>
            <div>
              <Label>Sexuality (optional)</Label>
              <Select value={form.sexuality || ""} onValueChange={(v) => setForm({ ...form, sexuality: v })}>
                <SelectTrigger data-testid="edit-sexuality" className="border-2 border-black bg-white"><SelectValue placeholder="Prefer not to say" /></SelectTrigger>
                <SelectContent>{SEXUALITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 bg-white border-2 border-black rounded-lg p-3" data-testid="social-links-editor">
              <Label className="text-sm font-bold flex items-center gap-1"><Globe className="w-4 h-4" />Social media links</Label>
              {(form.social_links || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select value={s.platform || "Instagram"} onValueChange={(v) => setForm({ ...form, social_links: form.social_links.map((x, j) => j === i ? { ...x, platform: v } : x) })}>
                    <SelectTrigger data-testid={`social-platform-${i}`} className="border-2 border-black bg-white w-32 shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOCIAL_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid={`social-url-${i}`} value={s.url || ""} onChange={(e) => setForm({ ...form, social_links: form.social_links.map((x, j) => j === i ? { ...x, url: e.target.value } : x) })} placeholder="https://…" className="border-2 border-black" />
                  <button type="button" data-testid={`social-remove-${i}`} onClick={() => setForm({ ...form, social_links: form.social_links.filter((_, j) => j !== i) })} className="text-spotter-maroon border-2 border-black rounded-md p-1.5 bg-white hover:bg-spotter-cream shrink-0"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" data-testid="social-add" onClick={() => setForm({ ...form, social_links: [...(form.social_links || []), { platform: "Instagram", url: "" }] })} className="border-2 border-black gap-1 text-xs"><Plus className="w-3.5 h-3.5" />Add a link</Button>
            </div>
            <div className="space-y-2 bg-white border-2 border-black rounded-lg p-3" data-testid="name-privacy-editor">
              <Label className="text-sm font-bold flex items-center gap-1"><Lock className="w-4 h-4" />Name privacy</Label>
              <label className="flex items-center gap-2 text-sm text-spotter-maroon">
                <input type="checkbox" data-testid="legal-name-private-toggle" checked={!!form.privacy?.legal_name} onChange={(e) => setForm({ ...form, privacy: { ...form.privacy, legal_name: e.target.checked } })} />
                Hide my legal name from the public (Owner & Admins always see it)
              </label>
              <label className="flex items-center gap-2 text-sm text-spotter-maroon">
                <input type="checkbox" data-testid="preferred-name-private-toggle" checked={!!form.privacy?.preferred_name} onChange={(e) => setForm({ ...form, privacy: { ...form.privacy, preferred_name: e.target.checked } })} />
                Hide my preferred name from the public
              </label>
              <p className="text-xs text-spotter-maroon">Your legal name and requests to change it are managed in <b>Settings</b>.</p>
            </div>
            <div>
              <Label>Profile song — paste a Spotify/Apple/YouTube link or embed code</Label>
              <Textarea data-testid="edit-song" value={form.song_url || ""} onChange={(e) => setForm({ ...form, song_url: e.target.value })} placeholder="If you want the full song to play on mobile, then you need to use YouTube, as Apple and Spotify only provide samples on mobile." className="border-2 border-black min-h-[70px] font-mono text-xs" />
              <p className="text-xs text-spotter-maroon mt-1" data-testid="song-help">If you want the full song to play on mobile, then you need to use YouTube, as Apple and Spotify only provide samples on mobile.</p>
              {form.song_url ? <Button type="button" size="sm" variant="outline" data-testid="clear-song" onClick={() => setForm({ ...form, song_url: "" })} className="mt-1 border-2 border-black gap-1 text-xs"><X className="w-3 h-3" />Remove song</Button> : null}
              {form.song_url ? (
                <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                  <input type="checkbox" data-testid="edit-song-autoplay" checked={!!form.song_autoplay} onChange={(e) => setForm({ ...form, song_autoplay: e.target.checked })} className="w-4 h-4 accent-spotter-maroon border-2 border-black" />
                  <span className="text-sm font-semibold text-spotter-maroon">Auto-play my song when someone opens my profile</span>
                </label>
              ) : null}
              {form.song_url && form.song_autoplay ? <p className="text-xs text-spotter-maroon/70 mt-1">Starts on the visitor's first tap/click (browsers block silent autoplay). Works best with a YouTube link.</p> : null}
            </div>
            <div className="space-y-2 bg-white border-2 border-black rounded-lg p-3">
              <Label className="text-sm font-bold">Privacy — hide from the public (Owner & Admins always see)</Label>
              {[["birthday", "Birthday"], ["location", "Location"], ["zodiac", "Zodiac"], ["relationship", "Relationship"]].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-sm">{label}</span>
                  <Switch data-testid={`privacy-${k}`} checked={!!form.privacy?.[k]} onCheckedChange={(v) => setForm({ ...form, privacy: { ...form.privacy, [k]: v } })} />
                </div>
              ))}
            </div>
            {myFrames.length > 0 && (
              <div className="space-y-2 bg-spotter-cream border-2 border-black rounded-lg p-3" data-testid="frame-picker">
                <Label className="text-sm font-bold flex items-center gap-1"><Crown className="w-4 h-4" />Profile frame <span className="text-[10px] font-normal text-spotter-maroon/60">(premium perk)</span></Label>
                <div className="flex flex-wrap gap-3 items-center">
                  <button type="button" data-testid="frame-none" onClick={() => chooseFrame(null)}
                    className={`w-14 h-14 rounded-full border-2 flex items-center justify-center text-xs font-bold ${!me?.profile_frame ? "border-spotter-maroon bg-white" : "border-black bg-white/60"}`}>None</button>
                  {myFrames.map((f) => (
                    <button type="button" key={f.key} data-testid={`frame-${f.key}`} onClick={() => chooseFrame(f.key)} title={f.name}
                      className={`w-16 h-16 rounded-xl border-2 bg-white p-0.5 ${me?.profile_frame === f.key ? "border-spotter-maroon ring-2 ring-spotter-gold" : "border-black"}`}>
                      <img src={f.url} alt={f.name} className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2 bg-spotter-cream border-2 border-black rounded-lg p-3">
              <Label className="text-sm font-bold flex items-center gap-1"><Heart className="w-4 h-4" />Request a relationship</Label>
              <Select value={relType} onValueChange={setRelType}>
                <SelectTrigger data-testid="rel-type" className="border-2 border-black bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["In a Relationship", "Engaged", "Married", "It's Complicated"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {relPartner ? (
                <div className="flex items-center gap-2 bg-white border-2 border-black rounded-lg p-2" data-testid="rel-partner-preview">
                  <Avatar user={relPartner} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{relPartner.name}</div>
                    <div className="text-xs text-spotter-maroon">@{relPartner.username}</div>
                  </div>
                  <span className="text-[10px] font-bold text-green-700">Correct person?</span>
                  <button data-testid="rel-partner-clear" onClick={() => { setRelPartner(null); setRelQuery(""); }} className="text-spotter-maroon"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="relative">
                  <Input data-testid="rel-partner" value={relQuery} onChange={(e) => setRelQuery(e.target.value)} placeholder="Search your partner by name or @username" className="border-2 border-black" />
                  {relResults.length > 0 && (
                    <div className="absolute z-40 mt-1 w-full bg-white border-2 border-black rounded-lg overflow-hidden brand-shadow-sm max-h-56 overflow-y-auto">
                      {relResults.slice(0, 6).map((u) => (
                        <button key={u.id} data-testid={`rel-result-${u.id}`} onClick={() => { setRelPartner(u); setRelResults([]); }}
                          className="w-full flex items-center gap-2 p-2 hover:bg-spotter-cream text-left">
                          <Avatar user={u} size="sm" /><span className="text-sm font-semibold">{u.name}</span><span className="text-xs text-spotter-maroon">@{u.username}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Input data-testid="rel-date" type="date" value={form.relationship_date || ""} onChange={(e) => setForm({ ...form, relationship_date: e.target.value })} className="border-2 border-black" />
              <div className="flex items-center justify-between"><span className="text-sm">Post publicly when confirmed</span><Switch data-testid="rel-public" checked={!!form.post_public} onCheckedChange={(v) => setForm({ ...form, post_public: v })} /></div>
              <Button data-testid="rel-request-btn" size="sm" disabled={!relPartner} onClick={requestRelationship} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Send {relType} request</Button>
            </div>
            <div>
              <Label>Profile background</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {backgrounds.map((b) => (
                  <button key={b.key} data-testid={`bg-option-${b.key}`} onClick={() => setForm({ ...form, background: b.key })}
                    className={`h-16 rounded-lg border-4 overflow-hidden ${form.background === b.key ? "border-spotter-maroon" : "border-black"}`} title={b.label}>
                    <img src={mediaUrl(b.url)} alt={b.label} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              {content.background_disclaimer && (
                <p className="text-xs text-spotter-maroon mt-2 italic" data-testid="bg-disclaimer">{content.background_disclaimer}</p>
              )}
            </div>
            {profile.requires_guardian && (
              <div>
                <Label>Appoint guardian (member username)</Label>
                <Input data-testid="edit-guardian" value={form.guardian_username || ""} onChange={(e) => setForm({ ...form, guardian_username: e.target.value })} placeholder="guardian_username" className="border-2 border-black" />
                <p className="text-xs text-spotter-maroon mt-1">{profile.guardian_id ? "A guardian is currently appointed." : "Required: your guardian approves all Pride requests."}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="save-profile-btn" onClick={saveProfile} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cover crop helper */}
      <Dialog open={coverDialog} onOpenChange={setCoverDialog}>        <DialogContent className="border-4 border-black bg-spotter-cream max-w-2xl" data-testid="cover-crop-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-spotter-maroon">Frame your cover (1920 × 1080)</DialogTitle></DialogHeader>
          <div className="w-full border-2 border-black overflow-hidden" style={{ aspectRatio: "16/9" }}>
            {tempCover && <img src={mediaUrl(tempCover)} alt="preview" className="w-full h-full object-cover" style={{ objectPosition: `center ${coverPos}%` }} />}
          </div>
          <div className="px-1">
            <Label>Vertical position</Label>
            <Slider data-testid="cover-pos-slider" value={[coverPos]} onValueChange={(v) => setCoverPos(v[0])} min={0} max={100} step={1} className="mt-2" />
          </div>
          <DialogFooter>
            <Button data-testid="save-cover-btn" onClick={saveCover} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Save cover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top 10 editor */}
      <Dialog open={viewsOpen} onOpenChange={setViewsOpen}>
        <DialogContent className="border-4 border-black bg-spotter-cream max-w-md" data-testid="profile-views-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-spotter-maroon flex items-center gap-2"><Eye className="w-5 h-5" />Who viewed your profile</DialogTitle></DialogHeader>
          {viewsData === null ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-spotter-maroon" /></div> : (
            viewsData.views.length === 0 ? <p className="text-sm text-spotter-maroon py-2 text-center">No profile views yet. (Owner, admin & moderator visits aren't counted.)</p> : (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {viewsData.views.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 border-2 border-black rounded-lg p-2 bg-white" data-testid={`view-row-${i}`}>
                    <button onClick={() => { setViewsOpen(false); navigate(`/profile/${v.viewer.id}`); }} className="flex items-center gap-2 flex-1 min-w-0">
                      <Avatar user={v.viewer} size="sm" />
                      <span className="text-sm font-semibold truncate">{v.viewer.name} <span className="text-xs text-spotter-maroon">@{v.viewer.username}</span></span>
                    </button>
                    <span className="text-xs text-spotter-maroon shrink-0">{v.count > 1 ? `${v.count}×` : ""} {v.last_viewed ? new Date(v.last_viewed).toLocaleDateString() : ""}</span>
                  </div>
                ))}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={topDialog} onOpenChange={setTopDialog}>        <DialogContent className="border-4 border-black bg-spotter-cream" data-testid="top-friends-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-spotter-maroon">Pick your Top Spots ({selectedTop.length}/12)</DialogTitle></DialogHeader>
          {selectedTop.length > 1 && (
            <div className="mb-1" data-testid="top-reorder">
              <p className="text-xs font-bold text-spotter-maroon mb-1">Your order — drag, or use the arrows</p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selectedTop.map((uid, idx) => {
                  const u = myFriends.find((f) => f.id === uid);
                  if (!u) return null;
                  return (
                    <div key={uid} draggable data-testid={`top-order-${uid}`}
                      onDragStart={() => setDragIdx(idx)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { if (dragIdx !== null && dragIdx !== idx) moveTop(dragIdx, idx); setDragIdx(null); }}
                      className="flex items-center gap-2 p-1.5 rounded-lg border-2 border-black bg-white">
                      <GripVertical className="w-4 h-4 text-spotter-maroon/60 cursor-move shrink-0" />
                      <span className="text-xs font-bold bg-spotter-gold border border-black rounded-full w-5 h-5 flex items-center justify-center shrink-0">{idx + 1}</span>
                      <Avatar user={u} size="sm" />
                      <span className="flex-1 text-sm font-semibold truncate">{u.name}</span>
                      <button type="button" data-testid={`top-up-${uid}`} disabled={idx === 0} onClick={() => moveTop(idx, idx - 1)} className="p-1 border-2 border-black rounded-md bg-white disabled:opacity-30 shrink-0"><ChevronUp className="w-4 h-4" /></button>
                      <button type="button" data-testid={`top-down-${uid}`} disabled={idx === selectedTop.length - 1} onClick={() => moveTop(idx, idx + 1)} className="p-1 border-2 border-black rounded-md bg-white disabled:opacity-30 shrink-0"><ChevronDown className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {myFriends.length === 0 ? <p className="text-sm text-spotter-maroon">Add some friends first!</p> : (
            <div className="max-h-80 overflow-y-auto space-y-2">
              {myFriends.map((u) => {
                const sel = selectedTop.includes(u.id);
                const order = selectedTop.indexOf(u.id) + 1;
                return (
                  <button key={u.id} data-testid={`top-pick-${u.id}`} onClick={() => toggleTop(u.id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-xl border-2 border-black text-left ${sel ? "bg-spotter-gold/30" : "bg-white"}`}>
                    <Avatar user={u} size="md" showPresence />
                    <div className="flex-1"><div className="font-bold text-sm">{u.name}</div><div className="text-xs text-spotter-maroon">@{u.username}</div></div>
                    {sel ? <span className="text-xs font-bold bg-spotter-gold border border-black rounded-full w-6 h-6 flex items-center justify-center">{order}</span> : <span className="text-xs text-spotter-maroon">Add</span>}
                  </button>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-top-friends-btn" onClick={saveTop} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Save Top Spots</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageCropper open={cropperOpen} onOpenChange={setCropperOpen} file={avatarFile} onCropped={onAvatarCropped} />

      {/* See all friends */}
      <Dialog open={seeAllOpen} onOpenChange={setSeeAllOpen}>
        <DialogContent className="border-4 border-black bg-spotter-cream max-h-[90dvh] overflow-y-auto overscroll-contain top-[5dvh] translate-y-0" data-testid="see-all-friends-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-spotter-maroon flex items-center gap-2"><Users className="w-5 h-5" />All Pride ({restFriends.length})</DialogTitle></DialogHeader>
          <div className="flex justify-end">
            <button data-testid="see-all-sort-toggle" onClick={() => setFriendSort((s) => s === "newest" ? "az" : "newest")}
              className="inline-flex items-center gap-1 text-xs font-bold border-2 border-black rounded-full px-3 py-1.5 bg-white"><ArrowUpDown className="w-3.5 h-3.5" />{friendSort === "newest" ? "Newest" : "A–Z"}</button>
          </div>
          <div className="space-y-2">
            {sortedRest.map((u) => (
              <button key={u.id} data-testid={`see-all-friend-${u.id}`} onClick={() => { navigate(`/profile/${u.id}`); setSeeAllOpen(false); }}
                className="w-full flex items-center gap-3 p-2 rounded-xl border-2 border-black bg-white text-left hover:bg-spotter-cream">
                <Avatar user={u} size="md" showPresence />
                <div className="flex-1"><div className="font-bold text-sm">{u.name}</div><div className="text-xs text-spotter-maroon">@{u.username}</div></div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo viewer */}
      <PhotoViewer photo={viewPhoto} ownerId={id} isOwner={isMe} canComment={isMe || fs.state === "friends"}
        photos={photos} onNavigate={setViewPhoto}
        open={!!viewPhoto} onClose={() => setViewPhoto(null)} />

      {/* Video viewer */}
      <VideoViewer video={viewVideo} isOwner={isMe} open={!!viewVideo} onClose={() => setViewVideo(null)} />

      {/* Report profile */}
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} targetType="user" targetId={id} targetName={profile.name} />

    </div>
  );
}
